import { message } from 'antd';
import { useIntl } from 'react-intl';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Settings } from 'CoreApi.Settings_V12';
import { handleErrorNotification } from 'Common/utils';
import { useApiClient } from '../../../utils/apiClient';
import reacQueryConfig from '../../../utils/apiClient/config';

export const useStoreSettings = (page: string) => {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['store', 'settings', page],
    queryFn: apiClient.Settings.StoreApi.getStoreSettings,
    retry: false,
    staleTime: 120000,
  });
};

export const useSaveStoreSettings = (page: string) => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const intl = useIntl();
  return useMutation({
    mutationFn: (data: Partial<Settings>) => {
      const currentSettings = queryClient.getQueryData([
        'store',
        'settings',
        page,
      ]) as Settings;
      const settingsMerged = {
        ...currentSettings,
        ...data,
      };
      queryClient.setQueryData(['store', 'settings', page], settingsMerged);
      return apiClient.Settings.StoreApi.postStoreSettings(settingsMerged);
    },
    ...reacQueryConfig.mutationInSensitive,
    onSuccess() {
      queryClient.refetchQueries({ queryKey: ['store', 'settings', page] });
      message.success(
        intl.formatMessage({ defaultMessage: 'Settings successfully saved.' })
      );
    },
    onError(error: any) {
      handleErrorNotification({
        error: error.error ?? error,
        lang: intl.locale.toLowerCase(),
      });
    },
  });
};

export const useReceiptPreview = () => {
  const apiClient = useApiClient();
  return useMutation({
    mutationFn: apiClient.Settings.StoreApi.getReceiptPreview,
  });
};

export const useUploadAccountImage = (page: string) => {
  const apiClient = useApiClient();
  const intl = useIntl();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: apiClient.Settings.StoreApi.uploadAccountImage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store', 'settings', page] });
      message.success(
        intl.formatMessage({
          defaultMessage: 'Image successfully uploaded',
        })
      );
    },
    onError: (error) => {
      handleErrorNotification({
        error,
        lang: intl.locale.toLowerCase(),
      });
    },
  });
};

export const useDeleteAccountImage = (page: string) => {
  const apiClient = useApiClient();
  const intl = useIntl();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: apiClient.Settings.StoreApi.deleteAccountImage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store', 'settings', page] });
      message.success(
        intl.formatMessage({
          defaultMessage: 'Image successfully deleted',
        })
      );
    },
    onError: (error) => {
      handleErrorNotification({
        error,
        lang: intl.locale.toLowerCase(),
      });
    },
  });
};

export const useUpdateAccountSync = () => {
  const intl = useIntl();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: apiClient.Settings.StoreApi.postAccountSync,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['account'],
      });
    },
    onError: (error) => {
      handleErrorNotification({
        error: JSON.parse(error.message),
        lang: intl.locale.toLowerCase(),
      });
    },
  });
};
